// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HasToasterContext, useToaster, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import StyledBox from "@rpsc/XDL/Components/Common/Box";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        homePageUrl: getHomePageUrl(state),
        // XNG-Change
        isGuestUser: getCurrentUserIsGuest(state),
        homePageURL: getPageLinkByPageType(state, "HomePage")?.url,
        createAccountPageURL: getPageLinkByPageType(state, "CreateAccountPage")?.url,
        allowMultipleWishLists: getSettingsCollection(state).wishListSettings.allowMultipleWishLists,
        order: getOrderState(state, cart?.orderNumber)
    };
};
// XNG-Change
const mapDispatchToProps = {
    addToWishList,
    setAddToListModalIsOpen,
};

// XNG-Change
type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    HasToasterContext &
    ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutConfirmationHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    emailSentText?: TypographyPresentationProps;
    divider?: InjectableCss;
    orderInfoGridItem?: GridItemProps;
    orderNumberText?: TypographyPresentationProps;
    orderPlacedText?: TypographyPresentationProps;
    deliveryMethodText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    printButton?: ButtonPresentationProps;
    // XNG-Change
    guestBoxHeader?: TypographyPresentationProps;
    guestBoxBody?: TypographyPresentationProps;
    guestBoxAction?: ButtonPresentationProps;
    continueShipping?: ButtonPresentationProps;
    guestBox?: InjectableCss;
}

export const headerStyles: OnePageCheckoutConfirmationHeaderStyles = {
    titleGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    titleText: {
        variant: "h2",
    },
    divider: {
        css: css`
            padding: 15px;
            height: 100%;
            width: 100%;
            overflow: hidden;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    orderInfoGridItem: {
        width: [8, 8, 10, 10, 10],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryMethodText: {
        css: css`
            margin-top: 10px;
        `,
    },
    orderNumberText: { variant: "h5" },
    buttonsGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            justify-content: flex-end;
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
};

const styles = headerStyles;

const Divider = styled.div<any>`
    ${injectCss}
`;

const OnePageCheckoutConfirmationHeader = ({
    cart,
    billTo,
    // XNG-Change
    isGuestUser,
    homePageURL,
    toaster,
    addToWishList,
    allowMultipleWishLists,
    setAddToListModalIsOpen,
    createAccountPageURL,
    order
}: Props) => {
    // XNG-Change
    const history = useHistory();

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    // XNG-Change
    const redirectToHome = () => {
        homePageURL && history.push(homePageURL);
    };
    const redirectToCreateAccount = () => {
        createAccountPageURL && history.push(createAccountPageURL);
    };

    const addAllToListClickHandler = () => {
        const productInfos =
            cart
                ?.cartLines!.filter(cartLine => cartLine.canAddToWishlist)
                .map(cartLine => {
                    return {
                        productId: cartLine.productId!,
                        qtyOrdered: cartLine.qtyOrdered!,
                        unitOfMeasure: cartLine.unitOfMeasure ?? "",
                    };
                }) || [];

        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toaster.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toaster.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    if (!cart || !billTo) {
        return null;
    }
    // XNG-Change: Change Labels
    return (
        <GridContainer {...styles.container}>
            {/* XNG-Changes -- moved to  OnePageCheckoutConfirmationSuccessMessage widget */}
            {/* <GridItem {...styles.titleGridItem}>
                <Typography as="h1" {...styles.titleText} data-test-selector="orderConfirmation_Success">
                    {siteMessage("OrderConfirmation_Success")}
                </Typography>
            </GridItem> */}
            <GridItem {...styles.orderInfoGridItem}>
                <Typography as="h2" {...styles.orderNumberText} data-test-selector="orderConfirmation_orderNumber">
                    {translate("Order #{0}", order.value?.erpOrderNumber || order.value?.webOrderNumber || "")}
                </Typography>

                <Typography as="p" {...styles.orderPlacedText} data-test-selector="orderConfirmation_orderPlaced">
                    {translate("Order Date")}
                    <Typography as="span">
                        <LocalizedDateTime
                            dateTime={cart.orderDate}
                            options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                        />
                    </Typography>
                </Typography>
                <Typography as="p" {...styles.orderPlacedText} data-test-selector="orderConfirmation_orderPlaced">
                    {translate("Status")}
                    <Typography as="span">{order.value?.statusDisplay}</Typography>
                </Typography>
                {/* <Typography as="p" {...styles.deliveryMethodText} data-test-selector="orderConfirmation_deliveryMethod">
                    {translate("Delivery method")}:&nbsp;
                    {cart.fulfillmentMethod === FulfillmentMethod.PickUp
                        ? translate("Store pickup at ") +
                          (cart.defaultWarehouse?.description || cart.defaultWarehouse?.name || "")
                        : cart.carrier && cart.shipVia
                        ? `${translate("Ship to home via")} ${cart.carrier.description} - ${cart.shipVia.description}`
                        : translate("Ship to home")}
                </Typography> */}
            </GridItem>
            <GridItem width={12}>
                <Typography as="p" {...styles.emailSentText} data-test-selector="orderConfirmation_EmailSent">
                    {siteMessage("OrderConfirmation_EmailSent", billTo.email)}
                </Typography>
            </GridItem>
            <GridItem {...styles.buttonsGridItem}>
                {/* XNG-Change Add Buttons */}

                <Button
                    {...styles.printButton}
                    onClick={printOrOpenPrintAllModal}
                    data-test-selector="orderConfirmation_printButton"
                    aria-label={translate("Print")}
                >
                    {translate("Print")}
                </Button>
                {!isGuestUser && (
                    <Button
                        {...styles.printButton}
                        onClick={addAllToListClickHandler}
                        data-test-selector="orderConfirmation_addToList"
                        aria-label={translate("Add to List")}
                    >
                        {translate("Add to List")}
                    </Button>
                )}
                <Button
                    {...styles.continueShipping}
                    onClick={redirectToHome}
                    data-test-selector="orderConfirmation_shipping"
                    aria-label={translate("Continue Shopping")}
                >
                    {translate("Continue Shopping")}
                </Button>
            </GridItem>
            {isGuestUser &&
                (!!siteMessage("Custom_Guest_Confirmation_Create_Account")?.toString()?.length ||
                    !!siteMessage("Custom_Guest_Confirmation_Create_Account_Title")?.toString()?.length) && (
                    <GridItem width={12}>
                        <StyledBox {...styles.guestBox}>
                            {!!siteMessage("Custom_Guest_Confirmation_Create_Account_Title")?.toString()?.length && (
                                <Typography {...styles.guestBoxHeader}>
                                    {siteMessage("Custom_Guest_Confirmation_Create_Account_Title")}
                                </Typography>
                            )}
                            {!!siteMessage("Custom_Guest_Confirmation_Create_Account")?.toString()?.length && (
                                <Typography {...styles.guestBoxBody}>
                                    {siteMessage("Custom_Guest_Confirmation_Create_Account")}
                                </Typography>
                            )}
                            <Button
                                {...styles.guestBoxAction}
                                onClick={redirectToCreateAccount}
                                aria-label={translate("Create an Account")}
                            >
                                {translate("Create an Account")}
                            </Button>
                        </StyledBox>
                    </GridItem>
                )}

            <GridItem width={12}>
                <Divider {...styles.divider} />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Change
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(OnePageCheckoutConfirmationHeader)),
    definition: {
        displayName: "Header",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
